import { reducedExerciseSets } from "@/lib/trainingRecordPRs";
import { isTrainingRecordComplete } from "@/lib/trainingRecordCompletion";
import { useMemo } from "react";
import { MOCK_COURSES, type ICourse, type CourseSystem } from "@/data/courses";
import { getExerciseById } from "@/data/exercises";
import {
  INJURY_PART_LABEL_REVERSE,
  type InjuryPart,
} from "@/data/injury-recovery-plan";
import type { TrainingDayType } from "@/data/training-plan-generator";
import {
  useTrainingRecords,
  useCustomPlan,
  useBodySignals,
} from "./useAppState";
import { useActiveWorkout } from "./useActiveWorkout";
import { useMemberTrainingPlan } from "./useMemberTrainingPlan";
import { useTrainingDate } from "./useTrainingSchedule";
import { resolveTrainingDay, memberDayId } from "@/lib/memberTrainingDay";

// ==================== 今日训练统一逻辑 ====================

export interface ITodayWorkoutInfo {
  /** 今日是否有训练计划 */
  hasPlan: boolean;
  /** 训练日 ID（今日应练） */
  dayId: string;
  /** 训练日名称 */
  dayName: string;
  /** 所属课程 ID */
  courseId: string;
  /** 所属课程名称 */
  courseName: string;
  /** 所属体系 */
  system: CourseSystem;
  /** 动作总数 */
  exerciseCount: number;
  /** 总组数 */
  totalSets: number;
  /** 预计时长（分钟） */
  durationMin: number;
  /** 今日已完成组数（含进行中的） */
  completedSets: number;
  /** 今日训练状态：not-started / in-progress / completed */
  status: "not-started" | "in-progress" | "completed";
  /** 今日是否已完整打卡 */
  isCompletedToday: boolean;
  /** 训练量调整：降量百分比（0/10/20） */
  volumeReductionPercent: number;
  /** 是否建议休息（三项异常时 true） */
  shouldRestToday: boolean;
  /** 异常指标数量（0-3） */
  abnormalIndicatorCount: number;
  /** 今日训练日类型（力量/有氧/主动恢复/休息等） */
  dayType: TrainingDayType | null;
  /** 伤病规避提示语（空串表示无） */
  injuryTip: string;
  /** 重度不适的部位（英文 key），用于训练页弹窗 */
  severeInjuryParts: string[];
}

/**
 * 获取今日训练计划（统一逻辑，首页 & 训练页 & 详情页都调用此函数）
 * 基于训练记录历史推算：
 *   - 若今日已完成记录 → 状态 completed
 *   - 若今日有部分记录 / 有进行中训练 → 状态 in-progress
 *   - 否则 → 状态 not-started
 * 训练日轮换：基于历史最后一次完成的训练日，推下一个该练的日
 *   （三分化推→拉→腿→推... 循环）
 */
export function useTodayWorkout(): ITodayWorkoutInfo {
  const { records } = useTrainingRecords();
  const { activeWorkout } = useActiveWorkout();
  const { plan, cycleDayDisplay } = useCustomPlan();
  const { memberPlan, getTodayWeekDay, trainingAdjustment } =
    useMemberTrainingPlan();
  const { activeSignals, severeParts } = useBodySignals();

  const today = useTrainingDate();

  // 伤病规避提示
  const injuryTip = useMemo(() => {
    if (activeSignals.length === 0) return "";
    const parts = [...new Set(activeSignals.map((s) => s.bodyPart))];
    const labels = parts
      .map((p) => INJURY_PART_LABEL_REVERSE[p as InjuryPart] || p)
      .join("、");
    return `已自动避开${labels}相关动作`;
  }, [activeSignals]);

  // ===== 优先：会员专属训练计划 =====
  if (memberPlan) {
    const todayDay = getTodayWeekDay();
    // 只有力量日才显示为训练计划；有氧/休息/主动恢复 视为非训练日
    if (todayDay && todayDay.type === "strength" && todayDay.courseDayId) {
      const resolved = resolveTrainingDay(todayDay.courseDayId, memberPlan);
      const course = resolved?.course;
      const courseDay = resolved?.day;
      if (course && courseDay) {
        const mainIds = courseDay.mainIds || [];
        const warmupIds = courseDay.warmupIds || [];
        const stretchIds = courseDay.stretchIds || [];
        const allExerciseIds = [...warmupIds, ...mainIds, ...stretchIds];

        let totalSets = 0;
        for (const exId of mainIds) {
          const ex = getExerciseById(exId);
          if (ex)
            totalSets +=
              (resolved && "configurations" in resolved
                ? resolved.configurations[exId]?.sets
                : undefined) ?? ex.sets;
        }

        const reduction = trainingAdjustment.reductionPercent / 100;
        let adjustedTotalSets = totalSets;
        let adjustedDuration = courseDay.durationMin;

        if (reduction > 0 && totalSets > 0) {
          // 力量训练：按比例减组数，至少减 1 组
          const reduceBy = Math.max(1, Math.round(totalSets * reduction));
          adjustedTotalSets = mainIds.reduce((sum, id) => {
            const ex = getExerciseById(id);
            const count =
              (resolved && "configurations" in resolved
                ? resolved.configurations[id]?.sets
                : undefined) ??
              ex?.sets ??
              0;
            return (
              sum +
              reducedExerciseSets(count, trainingAdjustment.reductionPercent)
            );
          }, 0);
          // 时长同步按比例减少
          adjustedDuration = Math.max(
            10,
            Math.round(courseDay.durationMin * (1 - reduction)),
          );
        }

        const todayRecords = records.filter(
          (r) =>
            (r.date === today ||
              (!("sourceKind" in r) &&
                r.date === new Date().toISOString().slice(0, 10))) &&
            (r.dayId === courseDay.id ||
              (!memberPlan.dailyOverrides?.[today] &&
                r.dayId ===
                  memberPlan.weekDays.find(
                    (d) => memberDayId(memberPlan, d) === courseDay.id,
                  )?.courseDayId)),
        );
        const completedSetsToday = todayRecords.reduce(
          (s, r) => s + r.completedSets,
          0,
        );
        const isCompletedToday = todayRecords.some(isTrainingRecordComplete);
        const isInProgress =
          activeWorkout.active && activeWorkout.dayId === courseDay.id;

        let completedSets = completedSetsToday;
        if (isInProgress && completedSets === 0) completedSets = 0;

        let status: "not-started" | "in-progress" | "completed" = "not-started";
        if (isCompletedToday) status = "completed";
        else if (isInProgress || completedSets > 0) status = "in-progress";

        return {
          hasPlan: true,
          dayId: courseDay.id,
          dayName: `${todayDay.label}·${todayDay.subtitle}`,
          courseId: course.id,
          courseName: memberPlan.name,
          system: memberPlan.courseSystem,
          exerciseCount: allExerciseIds.length,
          totalSets: adjustedTotalSets,
          durationMin: adjustedDuration,
          completedSets,
          status,
          isCompletedToday,
          volumeReductionPercent: trainingAdjustment.reductionPercent,
          shouldRestToday: trainingAdjustment.shouldRest,
          abnormalIndicatorCount: trainingAdjustment.abnormalCount,
          dayType: "strength",
          injuryTip,
          severeInjuryParts: severeParts,
        };
      }
    }
    // 非力量日或数据不全：返回无计划，由调用方展示休息/有氧/主动恢复提示
    // 降量数据仍透传，调用方可根据 dayType 决定是否展示及如何降量
    const nonStrengthDayType = todayDay?.type || "rest";
    const baseDuration = todayDay?.durationMin || 0;
    const reduction = trainingAdjustment.reductionPercent / 100;
    const adjustedDuration =
      reduction > 0 && baseDuration > 0
        ? Math.max(10, Math.round(baseDuration * (1 - reduction)))
        : baseDuration;

    return {
      hasPlan: false,
      dayId: "",
      dayName: todayDay ? todayDay.label : "休息",
      courseId:
        memberPlan.weekDays.find((d) => d.type === "strength")?.courseId || "",
      courseName: memberPlan.name,
      system: memberPlan.courseSystem,
      exerciseCount: 0,
      totalSets: 0,
      durationMin: adjustedDuration,
      completedSets: 0,
      status: "not-started",
      isCompletedToday: false,
      volumeReductionPercent: trainingAdjustment.reductionPercent,
      shouldRestToday: trainingAdjustment.shouldRest,
      abnormalIndicatorCount: trainingAdjustment.abnormalCount,
      dayType: nonStrengthDayType,
      injuryTip,
      severeInjuryParts: severeParts,
    };
  }

  // ===== 非会员：走原三分化逻辑 =====
  // 找到今日对应训练日（三分化，按 cycleDay % 3 推算）
  const findTodayDay = (): {
    day: {
      id: string;
      name: string;
      dayIndex?: number;
      focus?: string;
      durationMin: number;
      warmupIds?: string[];
      mainIds?: string[];
      stretchIds?: string[];
    };
    course: ICourse;
  } | null => {
    // 优先用定制方案的三分化课程
    const courses = MOCK_COURSES.filter(
      (c) => c.system === ("three-way" as CourseSystem),
    );
    const defaultCourse =
      courses.find((c) => c.id === plan.courseId) || courses[0];
    if (!defaultCourse || defaultCourse.days.length === 0) return null;

    // 用 cycleDayDisplay（基于 startDate 自动推进的天数）推算今日该练什么
    // 三分化 3 天一个循环，dayIndex 0→推, 1→拉, 2→蹲
    const dayCount = defaultCourse.days.length;
    const dayIdx = (((cycleDayDisplay - 1) % dayCount) + dayCount) % dayCount;
    const day = defaultCourse.days[dayIdx];
    return day ? { day, course: defaultCourse } : null;
  };

  const todayData = findTodayDay();

  if (!todayData) {
    return {
      hasPlan: false,
      dayId: "",
      dayName: "暂无训练计划",
      courseId: "",
      courseName: "",
      system: "three-way" as CourseSystem,
      exerciseCount: 0,
      totalSets: 0,
      durationMin: 0,
      completedSets: 0,
      status: "not-started",
      isCompletedToday: false,
      volumeReductionPercent: 0,
      shouldRestToday: false,
      abnormalIndicatorCount: 0,
      dayType: null,
      injuryTip: "",
      severeInjuryParts: [],
    };
  }

  const { day, course } = todayData;
  const mainIds = (day as unknown as { mainIds?: string[] }).mainIds || [];
  const warmupIds =
    (day as unknown as { warmupIds?: string[] }).warmupIds || [];
  const stretchIds =
    (day as unknown as { stretchIds?: string[] }).stretchIds || [];
  const allExerciseIds = [...warmupIds, ...mainIds, ...stretchIds];

  // 计算总组数（只算正式组）
  let totalSets = 0;
  for (const exId of mainIds) {
    const ex = getExerciseById(exId);
    if (ex) totalSets += ex.sets;
  }

  // 今日训练记录（同 dayId 的记录）
  const todayRecords = records.filter(
    (r) => r.date === today && r.dayId === day.id,
  );
  const completedSetsToday = todayRecords.reduce(
    (s, r) => s + r.completedSets,
    0,
  );
  const isCompletedToday = todayRecords.some(isTrainingRecordComplete);

  // 进行中：有 activeWorkout 且就是今日训练日
  const isInProgress = activeWorkout.active && activeWorkout.dayId === day.id;

  // 已完成过组数（进行中的 + 已保存记录）
  let completedSets = completedSetsToday;
  if (isInProgress && completedSets === 0) {
    // 进行中但还没存记录，也算有进度
    completedSets = 0;
  }

  let status: "not-started" | "in-progress" | "completed" = "not-started";
  if (isCompletedToday) {
    status = "completed";
  } else if (isInProgress || completedSets > 0) {
    status = "in-progress";
  }

  return {
    hasPlan: true,
    dayId: day.id,
    dayName: day.name,
    courseId: course.id,
    courseName: course.name,
    system: course.system,
    exerciseCount: allExerciseIds.length,
    totalSets,
    durationMin: day.durationMin,
    completedSets,
    status,
    isCompletedToday,
    volumeReductionPercent: 0,
    shouldRestToday: false,
    abnormalIndicatorCount: 0,
    dayType: "strength",
    injuryTip,
    severeInjuryParts: severeParts,
  };
}
